/**
 * IndexedDB storage for the media library.
 *
 * **Why not `localStorage`?** Everything else local in Wikistar (drafts,
 * the offline article library) lives in `localStorage`, but media can't:
 * it caps out around 5–10MB, stores strings only (so blobs would need
 * base64, inflating them ~33%), and is synchronous. A couple of phone
 * photos would exhaust the entire origin quota and take drafts and
 * offline articles down with them. IndexedDB stores `Blob`s natively,
 * is async, and gets orders of magnitude more space.
 *
 * Three object stores:
 *
 *  - `items`  — the metadata index (name, type, tags, alt, upload state),
 *               keyed by **`${pubkey}:${sha256}`** so each Nostr account
 *               using this browser has its own library. The pubkey half
 *               is `''` for anonymous items (added logged out, or
 *               migrated from the pre-scoping schema); those are claimed
 *               by the first account that opens the library while logged
 *               in — see `dbClaimAnonymousItems`.
 *  - `blobs`  — the actual file bytes, keyed by raw **sha256** and
 *               *shared* across accounts. Content-addressed means the
 *               same file is byte-identical whichever account added it,
 *               so per-account copies would double disk usage for no
 *               privacy gain (the bytes aren't identifying; the library
 *               contents are).
 *  - `thumbs` — small generated preview images, keyed by sha256, shared
 *               for the same reason.
 *
 * Keying by sha256 rather than a random id is deliberate: Blossom is
 * content-addressed, so the hash *is* the file's identity on every
 * server. That makes re-adding the same file idempotent (automatic
 * dedupe), lets us verify integrity, and means "is this already on
 * server X?" is answerable without any extra bookkeeping.
 */

const DB_NAME = 'wikistar-media';
const DB_VERSION = 2;

export const STORE_ITEMS = 'items';
export const STORE_BLOBS = 'blobs';
export const STORE_THUMBS = 'thumbs';

let dbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;

  dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB is not available in this browser.'));
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = (event) => {
      const db = request.result;

      if (event.oldVersion < 1) {
        // Fresh install at the current schema.
        // `id` is an in-line composite key on items; blobs/thumbs are
        // stored with out-of-line keys since a Blob can't carry its own
        // key property.
        db.createObjectStore(STORE_ITEMS, { keyPath: 'id' });
        db.createObjectStore(STORE_BLOBS);
        db.createObjectStore(STORE_THUMBS);
        return;
      }

      if (event.oldVersion < 2) {
        // v1 → v2: the items store was keyed by raw `sha256` with no
        // account attribution. Rebuild it keyed by `${pubkey}:${sha256}`.
        // Legacy items get an empty pubkey — they're claimed by the first
        // account that opens the library (see `dbClaimAnonymousItems`),
        // because the pre-scoping library on this browser belonged to
        // whoever was using it.
        const tx = (request as IDBOpenDBRequest).transaction;
        if (!tx || !db.objectStoreNames.contains(STORE_ITEMS)) return;
        const getAll = tx.objectStore(STORE_ITEMS).getAll();
        getAll.onsuccess = () => {
          const legacy = getAll.result as Array<Record<string, unknown>>;
          db.deleteObjectStore(STORE_ITEMS);
          const store = db.createObjectStore(STORE_ITEMS, { keyPath: 'id' });
          for (const item of legacy) {
            const sha256 = typeof item.sha256 === 'string' ? item.sha256 : '';
            if (!sha256) continue;
            store.put({ ...item, pubkey: '', id: mediaItemKey('', sha256) });
          }
        };
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('Failed to open the media database.'));
  });

  // A failed open shouldn't be cached forever — let the next call retry.
  dbPromise.catch(() => {
    dbPromise = null;
  });

  return dbPromise;
}

function runTransaction<T>(
  storeNames: string | string[],
  mode: IDBTransactionMode,
  work: (tx: IDBTransaction) => Promise<T> | T,
): Promise<T> {
  return openDb().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const tx = db.transaction(storeNames, mode);
        let result: T;

        // Resolve on `oncomplete`, not when the request callback fires:
        // for writes, only transaction completion means the data is
        // actually durable.
        tx.oncomplete = () => resolve(result);
        tx.onerror = () => reject(tx.error ?? new Error('Media database transaction failed.'));
        tx.onabort = () => reject(tx.error ?? new Error('Media database transaction aborted.'));

        Promise.resolve(work(tx))
          .then((value) => {
            result = value;
          })
          .catch((error) => {
            try {
              tx.abort();
            } catch {
              // Already aborting/finished.
            }
            reject(error);
          });
      }),
  );
}

function request<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('Media database request failed.'));
  });
}

/* ---------------- items (metadata) ---------------- */

/** The composite key the `items` store is keyed by: `${pubkey}:${sha256}`. */
export function mediaItemKey(pubkey: string, sha256: string): string {
  return `${pubkey}:${sha256}`;
}

/**
 * The subset of fields the store's own integrity logic needs to know
 * about. Full `MediaItem` typing lives in `media.ts`; the DB layer only
 * reads these when deciding whether a blob is still referenced.
 */
interface StoredMediaItem {
  id: string;
  pubkey: string;
  sha256: string;
  hasBlob: boolean;
}

export function dbGetAllItems<T>(): Promise<T[]> {
  return runTransaction(STORE_ITEMS, 'readonly', (tx) =>
    request<T[]>(tx.objectStore(STORE_ITEMS).getAll() as IDBRequest<T[]>),
  );
}

export function dbGetItem<T>(pubkey: string, sha256: string): Promise<T | undefined> {
  return runTransaction(STORE_ITEMS, 'readonly', (tx) =>
    request<T | undefined>(tx.objectStore(STORE_ITEMS).get(mediaItemKey(pubkey, sha256)) as IDBRequest<T | undefined>),
  );
}

export function dbPutItem(item: { pubkey: string; sha256: string }): Promise<void> {
  return runTransaction(STORE_ITEMS, 'readwrite', async (tx) => {
    await request(tx.objectStore(STORE_ITEMS).put({ ...item, id: mediaItemKey(item.pubkey, item.sha256) }));
  });
}

/**
 * Assigns every anonymous (`pubkey: ''`) item to `pubkey` — the migration
 * path for media added before the library was scoped per account, and for
 * files added while logged out. Returns the claimed items' hashes.
 *
 * If the account already has the same file (same sha256), the anonymous
 * entry is dropped rather than merged: the account's own copy has the
 * fresher metadata, and the underlying blob is shared either way.
 */
export function dbClaimAnonymousItems(pubkey: string): Promise<string[]> {
  return runTransaction(STORE_ITEMS, 'readwrite', async (tx) => {
    const store = tx.objectStore(STORE_ITEMS);
    const all = await request<StoredMediaItem[]>(store.getAll() as IDBRequest<StoredMediaItem[]>);
    const claimed: string[] = [];

    for (const item of all) {
      if (item.pubkey) continue;
      await request(store.delete(item.id));
      if (all.some((i) => i.pubkey === pubkey && i.sha256 === item.sha256)) continue;
      await request(store.put({ ...item, pubkey, id: mediaItemKey(pubkey, item.sha256) }));
      claimed.push(item.sha256);
    }

    return claimed;
  });
}

/* ---------------- blobs & thumbnails ---------------- */

export function dbGetBlob(sha256: string): Promise<Blob | undefined> {
  return runTransaction(STORE_BLOBS, 'readonly', (tx) =>
    request<Blob | undefined>(tx.objectStore(STORE_BLOBS).get(sha256) as IDBRequest<Blob | undefined>),
  );
}

export function dbGetThumb(sha256: string): Promise<Blob | undefined> {
  return runTransaction(STORE_THUMBS, 'readonly', (tx) =>
    request<Blob | undefined>(tx.objectStore(STORE_THUMBS).get(sha256) as IDBRequest<Blob | undefined>),
  );
}

/**
 * Writes metadata, bytes, and (optionally) a thumbnail in a single
 * transaction, so the library can never end up with an index entry
 * pointing at bytes that failed to store.
 */
export function dbPutMedia(item: { pubkey: string; sha256: string }, blob: Blob, thumb?: Blob): Promise<void> {
  return runTransaction([STORE_ITEMS, STORE_BLOBS, STORE_THUMBS], 'readwrite', async (tx) => {
    await request(tx.objectStore(STORE_ITEMS).put({ ...item, id: mediaItemKey(item.pubkey, item.sha256) }));
    await request(tx.objectStore(STORE_BLOBS).put(blob, item.sha256));
    if (thumb) await request(tx.objectStore(STORE_THUMBS).put(thumb, item.sha256));
  });
}

/**
 * Removes an item and all of its associated data together.
 *
 * The blob and thumbnail are shared across accounts (keyed by raw
 * sha256), so they're only deleted when no *other* account's item still
 * references the same file — otherwise one account removing a file would
 * silently break another's copy of it.
 */
export function dbDeleteMedia(pubkey: string, sha256: string): Promise<void> {
  return runTransaction([STORE_ITEMS, STORE_BLOBS, STORE_THUMBS], 'readwrite', async (tx) => {
    const itemStore = tx.objectStore(STORE_ITEMS);
    const items = await request<StoredMediaItem[]>(itemStore.getAll() as IDBRequest<StoredMediaItem[]>);
    await request(itemStore.delete(mediaItemKey(pubkey, sha256)));

    const stillReferenced = items.some((i) => i.sha256 === sha256 && i.pubkey !== pubkey);
    if (stillReferenced) return;

    await request(tx.objectStore(STORE_BLOBS).delete(sha256));
    await request(tx.objectStore(STORE_THUMBS).delete(sha256));
  });
}

/**
 * Drops only the full-size bytes, keeping the metadata and thumbnail.
 * Used by "free up space": once a file is safely hosted on a Blossom
 * server, the local original is a convenience rather than a necessity,
 * so the user can reclaim the disk while keeping a browsable library.
 *
 * Like `dbDeleteMedia`, the shared blob is only physically deleted when
 * no other account's item still holds it locally (`hasBlob`). If another
 * account does, the bytes stay — this item is marked evicted, and the
 * space is genuinely freed only once the last holder evicts it.
 */
export function dbDeleteBlobOnly(pubkey: string, sha256: string): Promise<void> {
  return runTransaction([STORE_ITEMS, STORE_BLOBS], 'readwrite', async (tx) => {
    const items = await request<StoredMediaItem[]>(
      tx.objectStore(STORE_ITEMS).getAll() as IDBRequest<StoredMediaItem[]>,
    );
    const heldElsewhere = items.some((i) => i.sha256 === sha256 && i.pubkey !== pubkey && i.hasBlob);
    if (heldElsewhere) return;
    await request(tx.objectStore(STORE_BLOBS).delete(sha256));
  });
}

/** Rough total of locally-stored bytes, for the storage summary. */
export async function dbEstimateUsage(): Promise<{ usage: number; quota: number } | undefined> {
  if (typeof navigator === 'undefined' || !navigator.storage?.estimate) return undefined;
  try {
    const { usage = 0, quota = 0 } = await navigator.storage.estimate();
    return { usage, quota };
  } catch {
    return undefined;
  }
}
